import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Select,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
  Paper,
  Divider,
  TextField
} from '@mui/material';
import {
  Route as RouteIcon,
  LocalShipping as TruckIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  GpsFixed as GpsIcon,
  Refresh as RefreshIcon,
  Tune as OptimizeIcon,
  PlayArrow as StartIcon,
  Today as TodayIcon,
  CheckCircle as CompleteIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';
import { formatScheduledDate } from '../../utils/dateUtils';
import GeofenceStatusChip from '../Common/GeofenceStatusChip';
import RouteProgressChip, { ROUTE_PROGRESS_REFRESH_MS } from '../RoutePlanning/RouteProgressChip';

// TKT-192: callers can opt out of the Optimize action. Naomi flagged
// that the button was exposed inside Summer/Winter Logs (which use the
// JobManagement -> Routes tab) and triggering it there could re-order
// a route by accident. Route Planning (RouteSetup) has its own
// dedicated optimize controls; here we default to false and let the
// host opt in explicitly.
const RouteViewer = ({ allowOptimize = false } = {}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Route data
  const [routeData, setRouteData] = useState(null);
  // Toronto date, not UTC: after 8 PM the UTC date is already tomorrow.
  const [selectedDate, setSelectedDate] = useState(
    new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })
  );
  const [selectedTruck, setSelectedTruck] = useState('');
  
  // Trucks for filtering
  const [trucks, setTrucks] = useState([]);

  // TKT-426: ahead/behind chip beside each truck, same as Routes list (TKT-420).
  const [routeProgress, setRouteProgress] = useState({}); // truckId -> progress

  // TKT-260: Print menu anchor. Naomi wants to print one truck's daily route
  // rather than the whole fleet, chosen at print time (independent of the
  // on-screen Truck Filter).
  const [printAnchor, setPrintAnchor] = useState(null);

  const openSchedulePrint = (truckId) => {
    const qs = new URLSearchParams({ date: selectedDate });
    if (truckId) qs.append('truck_id', truckId);
    window.open(`/schedule/print?${qs.toString()}`, '_blank', 'noopener');
    setPrintAnchor(null);
  };

  // ====================================
  // DATA FETCHING
  // ====================================

  const fetchRouteData = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      // TKT-478: include_snow adds snow-list stops (flagged is_snow_list).
      const params = new URLSearchParams({
        date: selectedDate,
        include_snow: 'true',
        ...(selectedTruck && { truck_id: selectedTruck })
      });

      const response = await makeApiCall(`api/routes/daily?${params}`);
      
      if (response.ok || response.status === 304) {
        // Handle both 200 OK and 304 Not Modified
        // For 304, the browser will use the cached response body
        const text = await response.text();
        let data = null;
        if (text) {
          data = JSON.parse(text);
        } else {
          // If no body (true 304), we need to fetch without cache
          const freshResponse = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/routes/daily?${params}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });
          if (!freshResponse.ok) throw new Error('Failed to fetch fresh data');
          data = await freshResponse.json();
        }
        setRouteData(data);
        // Ensure active_jobs exist for every stop, then re-fetch so any newly
        // created rows show up with active_job_id populated.
        const items = data?.route_items || [];
        // Snow-list stops only get a visit when the crew clocks in (TKT-467).
        const hadMissing = items.some((it) => !it.active_job_id && !it.is_snow_list);
        if (hadMissing) {
          await ensureActiveJobsForVisibleStops(selectedDate, items);
          // Bypass 304 cache with a no-store fetch
          const fresh = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/routes/daily?${params}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache',
            },
          });
          if (fresh.ok) {
            const refreshed = await fresh.json();
            setRouteData(refreshed);
          }
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load route data');
        setRouteData(null);
      }
    } catch (error) {
      console.error('Error fetching route data:', error);
      setError('Failed to load route data');
      setRouteData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrucks = async () => {
    try {
      const response = await makeApiCall('api/routes/trucks');
      if (response.ok) {
        const data = await response.json();
        setTrucks(data.trucks || []);
      }
    } catch (error) {
      console.error('Error fetching trucks:', error);
    }
  };

  // Ensure active_jobs exist for every stop shown. Called after fetchRouteData
  // so Clock In / Services buttons work without requiring users to first
  // "Create Daily Jobs" - that two-step flow was confusing and unnecessary.
  // The overnight scheduler pre-warms for free; this just covers the gap.
  const ensureActiveJobsForVisibleStops = async (date, items) => {
    if (!items || items.length === 0) return;
    const needsEnsure = items.filter((it) => !it.active_job_id && it.id && !it.is_snow_list);
    if (needsEnsure.length === 0) return;
    await Promise.all(needsEnsure.map(async (it) => {
      try {
        await makeApiCall('api/jobs/ensure-for-job-site', {
          method: 'POST',
          body: JSON.stringify({ job_site_id: it.id, scheduled_date: date }),
        });
      } catch (_) { /* non-fatal */ }
    }));
  };

  const handleOptimizeRoute = async (truckId) => {
    try {
      const dayOfWeek = new Date(selectedDate).getDay();
      const workingDay = dayOfWeek === 0 ? 7 : dayOfWeek;

      // Call the new Mapbox-backed /optimize (preview) and then commit the
      // suggested order via /reorder.
      const previewRes = await makeApiCall('api/routes/optimize', {
        method: 'POST',
        body: JSON.stringify({
          truck_id: truckId,
          day_of_week: workingDay,
          avoid_tolls: true
        })
      });

      if (!previewRes.ok) {
        const errorData = await previewRes.json().catch(() => ({}));
        setError(errorData.error || 'Failed to optimize route');
        return;
      }

      const preview = await previewRes.json();
      const orderedIds = preview.optimized_order || [];
      if (orderedIds.length === 0) {
        setError('Optimization returned no stops');
        return;
      }

      const applyRes = await makeApiCall('api/routes/reorder', {
        method: 'POST',
        body: JSON.stringify({
          truck_id: truckId,
          day_of_week: workingDay,
          ordered_job_site_ids: orderedIds,
        })
      });

      if (applyRes.ok) {
        setSuccess('Route optimized successfully');
        fetchRouteData();
      } else {
        const errorData = await applyRes.json().catch(() => ({}));
        setError(errorData.error || 'Failed to apply optimized order');
      }
    } catch (error) {
      console.error('Error optimizing route:', error);
      setError('Failed to optimize route');
    }
  };

  // ====================================
  // UTILITY FUNCTIONS
  // ====================================

  const getJobStatusColor = (routeItem) => {
    if (!routeItem.active_job_id) return 'default';
    if (routeItem.is_complete) return 'success';
    if (routeItem.clock_in_time && !routeItem.clock_out_time) return 'warning';
    return 'info';
  };

  const getJobStatusText = (routeItem) => {
    if (!routeItem.active_job_id) return 'Not Started';
    if (routeItem.is_complete) return 'Completed';
    if (routeItem.clock_in_time && !routeItem.clock_out_time) return 'In Progress';
    return 'Ready';
  };

  const groupRoutesByTruck = (routeItems) => {
    const grouped = {};
    
    routeItems.forEach(item => {
      const truckKey = item.truck_id || 'unassigned';
      if (!grouped[truckKey]) {
        grouped[truckKey] = {
          truck_id: item.truck_id,
          truck_name: item.truck_name || 'Unassigned',
          items: []
        };
      }
      grouped[truckKey].items.push(item);
    });

    // Sort items within each truck by priority
    Object.values(grouped).forEach(truck => {
      truck.items.sort((a, b) => (a.priority || 999) - (b.priority || 999));
    });

    return grouped;
  };

  // ====================================
  // EFFECTS
  // ====================================

  useEffect(() => {
    fetchTrucks();
  }, []);

  useEffect(() => {
    fetchRouteData();
  }, [selectedDate, selectedTruck]);

  useEffect(() => {
    let cancelled = false;
    setRouteProgress({});
    const loadProgress = () => {
      makeApiCall(`api/routes/live-progress?date=${encodeURIComponent(selectedDate)}`)
        .then((response) => (response.ok ? response.json() : { trucks: [] }))
        .then((payload) => {
          if (cancelled) return;
          const byTruck = {};
          (payload.trucks || []).forEach((truck) => {
            byTruck[truck.truck_id] = truck;
          });
          setRouteProgress(byTruck);
        })
        .catch(() => {
          // Inline chips only — never surface errors for them.
        });
    };
    loadProgress();
    // Only today's numbers move; past and future days load once.
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
    const id = selectedDate === today ? setInterval(loadProgress, ROUTE_PROGRESS_REFRESH_MS) : null;
    return () => {
      cancelled = true;
      if (id) clearInterval(id);
    };
  }, [selectedDate]);

  // ====================================
  // RENDER FUNCTIONS
  // ====================================

  const renderRouteControls = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              type="date"
              label="Route Date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Truck Filter</InputLabel>
              <Select
                value={selectedTruck}
                label="Truck Filter"
                onChange={(e) => setSelectedTruck(e.target.value)}
              >
                <MenuItem value="">All Trucks</MenuItem>
                {trucks.map((truck) => (
                  <MenuItem key={truck.id} value={truck.id}>
                    {truck.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <Box display="flex" gap={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={(e) => setPrintAnchor(e.currentTarget)}
                disabled={loading}
                aria-haspopup="menu"
                aria-label={t('common.print')}
              >
                {t('common.print')}
              </Button>
              {/* TKT-260: pick which truck's daily route to print (or all). */}
              <Menu
                anchorEl={printAnchor}
                open={Boolean(printAnchor)}
                onClose={() => setPrintAnchor(null)}
              >
                <MenuItem onClick={() => openSchedulePrint('')}>
                  {t('routeViewer.printAllTrucks', 'All Trucks')}
                </MenuItem>
                {trucks.map((truck) => (
                  <MenuItem key={truck.id} onClick={() => openSchedulePrint(truck.id)}>
                    {t('routeViewer.printTruck', 'Truck')} {truck.name}
                  </MenuItem>
                ))}
              </Menu>

              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchRouteData}
                disabled={loading}
                aria-label={t('common.refresh')}
              >
                {t('common.refresh')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  // TKT-431: show each stop's allotted work time (from its contract), not
  // the flat 15-minute default drive time every stop carried.
  const formatMinutes = (mins) => {
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
    if (hours === 0) return t('routeViewer.minutes', { count: minutes });
    return t('routeViewer.hoursMinutes', { hours, minutes });
  };

  const renderRouteCard = (truck) => {
    // TKT-478: snow-list stops are listed in their own section below the
    // weekday route and don't count toward the planned stops or time.
    const weekdayItems = truck.items.filter((item) => !item.is_snow_list);
    const snowItems = truck.items.filter((item) => item.is_snow_list);
    const totalAllotted = weekdayItems.reduce(
      (sum, item) => sum + (Number(item.allotted_minutes) > 0 ? Number(item.allotted_minutes) : 0),
      0
    );

    return (
    <Card key={truck.truck_id || 'unassigned'} sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center">
            <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
              <TruckIcon />
            </Avatar>
            <Box>
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                <Typography variant="h6">
                  {truck.truck_name}
                </Typography>
                {truck.truck_id && <RouteProgressChip progress={routeProgress[truck.truck_id]} />}
              </Box>
              <Typography variant="body2" color="textSecondary">
                {t('routeViewer.stopsPlanned', { count: weekdayItems.length })}
                {totalAllotted > 0 && ` • ${t('routeViewer.allottedTotal', { time: formatMinutes(totalAllotted) })}`}
                {snowItems.length > 0 && ` • ${t('routeViewer.snowListCount', { count: snowItems.length })}`}
              </Typography>
            </Box>
          </Box>
          
          {/* TKT-192: only render the Optimize control when the host
              opted in. Hides the button on the Summer/Winter/Construction
              Logs Routes tab so dispatchers don't accidentally re-order
              a route from a logs page. Optimize still lives on the
              Route Planning page where it's the primary workflow. */}
          {allowOptimize && (
            <Box display="flex" gap={1}>
              <Tooltip title="Optimize Route">
                <span>
                  <IconButton
                    onClick={() => handleOptimizeRoute(truck.truck_id)}
                    disabled={!truck.truck_id || truck.items.length < 2}
                    aria-label="Optimize this route"
                  >
                    <OptimizeIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>

        <List dense>
          {weekdayItems.map((item, index) => (
            <React.Fragment key={item.job_site_id}>
              <ListItem>
                <ListItemIcon>
                  <Avatar sx={{ width: 24, height: 24, fontSize: '0.875rem' }}>
                    {item.priority || index + 1}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
                      <Typography variant="subtitle2">
                        {item.company_name || `${item.first_name} ${item.last_name}`}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <GeofenceStatusChip
                          status={item.gps_status}
                          timeIn={item.gps_time_in}
                          timeOut={item.gps_time_out}
                          allottedMinutes={item.allotted_minutes}
                          size="small"
                        />
                        <Chip
                          label={getJobStatusText(item)}
                          color={getJobStatusColor(item)}
                          size="small"
                        />
                      </Box>
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {item.street_address}, {item.city}
                      </Typography>
                      {item.quote_number && (
                        <Typography variant="caption" color="textSecondary">
                          {t('routeViewer.quoteRef', { number: item.quote_number, amount: item.total_amount })}
                        </Typography>
                      )}
                      <Typography variant="caption" color="textSecondary" sx={{ ml: item.quote_number ? 2 : 0 }}>
                        {Number(item.allotted_minutes) > 0
                          ? t('routeViewer.allottedStop', { time: formatMinutes(Number(item.allotted_minutes)) })
                          : t('routeViewer.allottedNotSet')}
                      </Typography>
                    </Box>
                  }
                />
                {item.active_job_id && (
                  <Box display="flex" alignItems="center" ml={1}>
                    {item.clock_in_time && (
                      <Tooltip title={`Started: ${new Date(item.clock_in_time).toLocaleTimeString()}`}>
                        <StartIcon color="success" fontSize="small" />
                      </Tooltip>
                    )}
                    {item.is_complete && (
                      <Tooltip title={`Completed: ${new Date(item.clock_out_time).toLocaleTimeString()}`}>
                        <CompleteIcon color="primary" fontSize="small" />
                      </Tooltip>
                    )}
                  </Box>
                )}
              </ListItem>
              {index < weekdayItems.length - 1 && <Divider />}
            </React.Fragment>
          ))}

          {snowItems.length > 0 && (
            <ListItem sx={{ pt: weekdayItems.length > 0 ? 2 : 0 }}>
              <Typography variant="overline" color="textSecondary">
                {t('routeViewer.snowListHeading')}
              </Typography>
            </ListItem>
          )}
          {snowItems.map((item, index) => (
            <React.Fragment key={`snow-${item.id}`}>
              <ListItem>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
                      <Typography variant="subtitle2">
                        {item.company_name || `${item.first_name} ${item.last_name}`}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Chip label={t('routeViewer.snowChip')} color="info" size="small" variant="outlined" />
                        {Number(item.completed_visits_today) > 0 && (
                          <Chip
                            label={t('routeViewer.snowVisitsToday', { count: Number(item.completed_visits_today) })}
                            color="primary"
                            size="small"
                          />
                        )}
                        {item.active_job_id && !item.is_complete && !item.is_cancelled && item.clock_in_time && (
                          <Chip label={getJobStatusText(item)} color={getJobStatusColor(item)} size="small" />
                        )}
                      </Box>
                    </Box>
                  }
                  secondary={
                    <Typography variant="body2" color="textSecondary">
                      {item.street_address}, {item.city}
                    </Typography>
                  }
                />
              </ListItem>
              {index < snowItems.length - 1 && <Divider />}
            </React.Fragment>
          ))}
          
          {truck.items.length === 0 && (
            <ListItem>
              <ListItemText 
                primary="No stops scheduled"
                secondary="No job sites assigned to this truck for the selected date"
              />
            </ListItem>
          )}
        </List>
      </CardContent>
    </Card>
  )};

  // ====================================
  // MAIN RENDER
  // ====================================

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Daily Routes
      </Typography>
      
      {renderRouteControls()}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box>
          <LinearProgress />
          <Typography sx={{ mt: 2, textAlign: 'center' }}>
            Loading route data...
          </Typography>
        </Box>
      ) : routeData && Array.isArray(routeData.route_items) ? (
        <Box>
          {routeData.route_items.length > 0 ? (
            <>
              <Box display="flex" alignItems="center" justifyContent="between" mb={2}>
                <Typography variant="h6">
                  Routes for {formatScheduledDate(selectedDate)}
                </Typography>
                <Chip 
                  label={`${routeData.route_items.length} total stops`} 
                  color="primary" 
                />
              </Box>

              {Object.values(groupRoutesByTruck(routeData.route_items)).map(truck => 
                renderRouteCard(truck)
              )}
            </>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <TodayIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {t('routeViewer.noRoutesTitle')}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {t('routeViewer.noRoutesSubtitle', {
                  date: formatScheduledDate(selectedDate),
                  // Build the weekday from local date parts so it doesn't
                  // drift across UTC midnight in Eastern Time.
                  weekday: (() => {
                    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(selectedDate));
                    const d = m
                      ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
                      : new Date(selectedDate);
                    return d.toLocaleDateString(undefined, { weekday: 'long' });
                  })(),
                })}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                {t('routeViewer.noRoutesHelp')}
              </Typography>
            </Paper>
          )}
        </Box>
      ) : (
        <Alert severity="info">
          No route data available. Click Refresh to reload.
        </Alert>
      )}
    </Box>
  );
};

export default RouteViewer;
